import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Megaphone, RefreshCw, Loader2, TrendingUp, Users, CheckCircle2, Globe,
  Plus, Trash2, BarChart3, Share2, Info,
} from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import AIVisibility from "./AIVisibility";
import CompetitorSpy from "./CompetitorSpy";
import AIStatusBadge from "./AIStatusBadge";
import AdConnectors from "./AdConnectors";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const num = (n) => (n ?? 0).toLocaleString();

function StatCard({ icon: Icon, label, value, tone = "#8A6D00", testid }) {
  return (
    <Card className="bg-[#FFFFFF] border-[#DAD5CA]" data-testid={testid}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${tone}1A` }}>
          <Icon className="w-5 h-5" style={{ color: tone }} />
        </div>
        <div>
          <div className="text-xl font-semibold text-[#1A1A1A]">{value}</div>
          <div className="text-xs text-[#8A857A]">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function ChannelTable({ rows, testid }) {
  if (!rows?.length) return <div className="p-6 text-center text-xs text-[#8A857A]">No data in this period.</div>;
  return (
    <div className="overflow-hidden rounded-lg border border-[#DAD5CA]" data-testid={testid}>
      <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-[#DAD5CA] text-[10px] uppercase tracking-wider text-[#8A857A] font-semibold">
        <div className="col-span-5">Channel</div>
        <div className="col-span-2 text-right">Leads</div>
        <div className="col-span-2 text-right">Pipeline</div>
        <div className="col-span-2 text-right">Won</div>
        <div className="col-span-1 text-right">CVR</div>
      </div>
      {rows.map((r) => (
        <div key={r.name} className="grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-[#F4F2ED] text-sm items-center">
          <div className="col-span-5 text-[#1A1A1A] truncate">{r.name}</div>
          <div className="col-span-2 text-right text-[#59554D]">{num(r.leads)}</div>
          <div className="col-span-2 text-right text-[#6B5600]">{num(r.in_pipeline)}</div>
          <div className="col-span-2 text-right text-[#0E6B3F]">{num(r.converted)}</div>
          <div className="col-span-1 text-right text-[#8A857A]">{r.conversion_rate}%</div>
        </div>
      ))}
    </div>
  );
}

const emptyMetric = { network: "linkedin", metric_date: new Date().toISOString().slice(0, 10), followers: "", impressions: "", engagement: "", clicks: "", spend: "", leads: "", notes: "" };

export default function MarketingModule({ view = "main" }) {
  const isSub = ["social", "ai", "spy"].includes(view);
  const [mainTab, setMainTab] = useState("overview");
  const [days, setDays] = useState("90");
  const [attr, setAttr] = useState(null);
  const [ga4, setGa4] = useState({ configured: false });
  const [social, setSocial] = useState({ metrics: [], latest: [], networks: [] });
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyMetric);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [a, g, s] = await Promise.all([
        axios.get(`${API}/marketing/attribution?days=${days}`),
        axios.get(`${API}/marketing/website/status`),
        axios.get(`${API}/marketing/social-metrics`),
      ]);
      setAttr(a.data);
      setGa4(g.data);
      setSocial(s.data);
    } catch (e) {
      toast.error("Failed to load marketing data");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const addMetric = async () => {
    if (!form.network || !form.metric_date) { toast.error("Network and date are required"); return; }
    setSaving(true);
    try {
      const payload = { network: form.network, metric_date: form.metric_date, notes: form.notes };
      ["followers", "impressions", "engagement", "clicks", "leads"].forEach(k => { if (form[k] !== "") payload[k] = parseInt(form[k], 10); });
      if (form.spend !== "") payload.spend = parseFloat(form.spend);
      await axios.post(`${API}/marketing/social-metrics`, payload);
      toast.success("Metric added");
      setForm({ ...emptyMetric, network: form.network });
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add metric");
    } finally {
      setSaving(false);
    }
  };

  const deleteMetric = async (id) => {
    try {
      await axios.delete(`${API}/marketing/social-metrics/${id}`);
      toast.success("Deleted");
      fetchAll();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const totals = attr?.totals || {};

  const VIEW_META = {
    main: { title: "Marketing", subtitle: "Channels → leads → pipeline, tied to the UTMs captured on every inbound lead" },
    social: { title: "Social & Ads", subtitle: "Weekly follower, engagement and ad-spend figures by network" },
  };
  const meta = VIEW_META[view] || VIEW_META.main;
  const showHeader = view === "main" || view === "social";

  return (
    <div className="space-y-6" data-testid="marketing-view">
      {showHeader && (
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#EFF6FF] flex items-center justify-center">
            <Megaphone className="w-5 h-5 text-[#1D4ED8]" />
          </div>
          <div>
            <h2 className="heading text-xl text-[#1A1A1A] tracking-tight">{meta.title}</h2>
            <p className="text-sm text-[#59554D]">{meta.subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <AIStatusBadge />
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-[150px] bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="marketing-period"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last 12 months</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchAll} disabled={loading} className="border-[#DAD5CA] bg-transparent text-[#59554D] hover:bg-[#F4F2ED]" data-testid="marketing-refresh">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>
      )}

      <Tabs value={isSub ? view : mainTab} onValueChange={(v) => { if (!isSub) setMainTab(v); }}>
        {!isSub && (
          <TabsList className="bg-[#FFFFFF] border border-[#DAD5CA]">
            <TabsTrigger value="overview" data-testid="marketing-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="attribution" data-testid="marketing-tab-attribution">Attribution</TabsTrigger>
            <TabsTrigger value="website" data-testid="marketing-tab-website">Website</TabsTrigger>
            <TabsTrigger value="setup" data-testid="marketing-tab-setup">Setup</TabsTrigger>
          </TabsList>
        )}

        {/* Overview */}
        <TabsContent value="overview" className="space-y-4 mt-4" data-testid="marketing-overview">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard icon={Users} label="Leads" value={num(totals.leads)} tone="#8A6D00" testid="mk-stat-leads" />
            <StatCard icon={TrendingUp} label="In Pipeline" value={num(totals.in_pipeline)} tone="#6B5600" testid="mk-stat-pipeline" />
            <StatCard icon={CheckCircle2} label="Converted" value={num(totals.converted)} tone="#0E6B3F" testid="mk-stat-converted" />
            <StatCard icon={BarChart3} label="Conversion Rate" value={`${totals.conversion_rate ?? 0}%`} tone="#1D4ED8" testid="mk-stat-cvr" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-[#8A857A] font-semibold mb-2">Leads by channel</div>
            <ChannelTable rows={attr?.by_channel} testid="mk-overview-channels" />
          </div>
          {!ga4.configured && (
            <div className="rounded-lg border border-[#FFF8D6] bg-[#FFF8D6] p-3 flex items-start gap-2 text-xs text-[#59554D]" data-testid="mk-ga4-notice">
              <Info className="w-4 h-4 text-[#8A6D00] shrink-0 mt-0.5" />
              <span>Website Analytics (GA4) isn't connected yet — add the service-account JSON and Property ID to light up traffic data. Attribution above already works from your lead UTMs.</span>
            </div>
          )}
        </TabsContent>

        {/* Attribution */}
        <TabsContent value="attribution" className="space-y-4 mt-4" data-testid="marketing-attribution">
          <div>
            <div className="text-xs uppercase tracking-wider text-[#8A857A] font-semibold mb-2">By channel</div>
            <ChannelTable rows={attr?.by_channel} testid="mk-attr-channels" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-[#8A857A] font-semibold mb-2">By campaign (UTM)</div>
              <ChannelTable rows={attr?.by_campaign} testid="mk-attr-campaigns" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-[#8A857A] font-semibold mb-2">By source</div>
              <ChannelTable rows={attr?.by_source} testid="mk-attr-sources" />
            </div>
          </div>
        </TabsContent>

        {/* Website */}
        <TabsContent value="website" className="space-y-4 mt-4" data-testid="marketing-website">
          {ga4.configured ? (
            <GA4Panel />
          ) : (
            <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
              <CardContent className="p-8 text-center space-y-3">
                <Globe className="w-10 h-10 mx-auto text-[#8A857A]" />
                <div className="text-[#1A1A1A] font-medium">Connect Google Analytics 4</div>
                <p className="text-sm text-[#59554D] max-w-md mx-auto">
                  In Google Cloud: enable the Analytics Data API, create a service account, download its JSON key,
                  then add the service-account email as a <b>Viewer</b> in GA4 (Admin → Property Access Management).
                  Provide the JSON key and the numeric GA4 Property ID and this panel lights up.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Social & Ads */}
        <TabsContent value="social" className="space-y-4 mt-4" data-testid="marketing-social">
          <AdConnectors />
          <div className="pt-2 border-t border-[#F4F2ED]">
            <div className="text-xs uppercase tracking-wider text-[#8A857A] font-semibold mb-3">Organic social (manual entry)</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {social.latest?.map((m) => (
              <StatCard key={m.network} icon={Share2} label={`${m.network_label} followers`} value={num(m.followers)} tone="#1D4ED8" testid={`mk-social-latest-${m.network}`} />
            ))}
          </div>
          <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
            <CardContent className="p-4 space-y-3">
              <div className="text-xs uppercase tracking-wider text-[#8A857A] font-semibold">Add weekly figures (manual — until API access is approved)</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="text-xs text-[#59554D]">Network</label>
                  <Select value={form.network} onValueChange={(v) => setForm({ ...form, network: v })}>
                    <SelectTrigger className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="mk-form-network"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                      {social.networks?.map(n => <SelectItem key={n.key} value={n.key}>{n.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><label className="text-xs text-[#59554D]">Date</label><Input type="date" value={form.metric_date} onChange={(e) => setForm({ ...form, metric_date: e.target.value })} className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="mk-form-date" /></div>
                <div><label className="text-xs text-[#59554D]">Followers</label><Input type="number" value={form.followers} onChange={(e) => setForm({ ...form, followers: e.target.value })} className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="mk-form-followers" /></div>
                <div><label className="text-xs text-[#59554D]">Impressions</label><Input type="number" value={form.impressions} onChange={(e) => setForm({ ...form, impressions: e.target.value })} className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="mk-form-impressions" /></div>
                <div><label className="text-xs text-[#59554D]">Engagement</label><Input type="number" value={form.engagement} onChange={(e) => setForm({ ...form, engagement: e.target.value })} className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="mk-form-engagement" /></div>
                <div><label className="text-xs text-[#59554D]">Clicks</label><Input type="number" value={form.clicks} onChange={(e) => setForm({ ...form, clicks: e.target.value })} className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="mk-form-clicks" /></div>
                <div><label className="text-xs text-[#59554D]">Spend ($)</label><Input type="number" value={form.spend} onChange={(e) => setForm({ ...form, spend: e.target.value })} className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="mk-form-spend" /></div>
                <div><label className="text-xs text-[#59554D]">Leads</label><Input type="number" value={form.leads} onChange={(e) => setForm({ ...form, leads: e.target.value })} className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="mk-form-leads" /></div>
              </div>
              <div className="flex justify-end">
                <Button onClick={addMetric} disabled={saving} className="bg-[#1D4ED8] hover:bg-[#EFF6FF] text-[#1A1A1A] font-medium" data-testid="mk-form-save">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />} Add
                </Button>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
            <CardContent className="p-0">
              {social.metrics?.length === 0 ? (
                <div className="p-8 text-center text-sm text-[#8A857A]">No figures yet — add your first weekly entry above.</div>
              ) : (
                <div>
                  <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-[#DAD5CA] text-[10px] uppercase tracking-wider text-[#8A857A] font-semibold">
                    <div className="col-span-2">Network</div><div className="col-span-2">Date</div>
                    <div className="col-span-2 text-right">Followers</div><div className="col-span-2 text-right">Impr.</div>
                    <div className="col-span-1 text-right">Eng.</div><div className="col-span-2 text-right">Spend</div><div className="col-span-1 text-right"></div>
                  </div>
                  {social.metrics.map((m) => (
                    <div key={m.id} className="grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-[#F4F2ED] text-sm items-center" data-testid={`mk-metric-row-${m.id}`}>
                      <div className="col-span-2"><Badge className="bg-[#1D4ED8] text-[#1D4ED8] border-0 text-[10px]">{m.network_label}</Badge></div>
                      <div className="col-span-2 text-[#59554D]">{m.metric_date}</div>
                      <div className="col-span-2 text-right text-[#59554D]">{num(m.followers)}</div>
                      <div className="col-span-2 text-right text-[#59554D]">{num(m.impressions)}</div>
                      <div className="col-span-1 text-right text-[#59554D]">{num(m.engagement)}</div>
                      <div className="col-span-2 text-right text-[#0E6B3F]">{m.spend != null ? `$${num(m.spend)}` : "—"}</div>
                      <div className="col-span-1 text-right">
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-[#C23A00] hover:bg-[#FFE9E0]" onClick={() => deleteMetric(m.id)} data-testid={`mk-metric-delete-${m.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Setup */}
        <TabsContent value="setup" className="space-y-4 mt-4" data-testid="marketing-setup">
          <GA4Setup onChange={fetchAll} />
        </TabsContent>

        {/* AI Visibility */}
        <TabsContent value="ai" className="space-y-4 mt-4" data-testid="marketing-ai">
          <AIVisibility />
        </TabsContent>

        <TabsContent value="spy" className="space-y-4 mt-4" data-testid="marketing-spy">
          <CompetitorSpy />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function GA4Setup({ onChange }) {
  const [status, setStatus] = useState(null);
  const [propertyId, setPropertyId] = useState("");
  const [fileName, setFileName] = useState("");
  const [jsonText, setJsonText] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const loadStatus = useCallback(() => {
    axios.get(`${API}/marketing/config`).then(r => {
      setStatus(r.data);
      if (r.data.property_id) setPropertyId(r.data.property_id);
    }).catch(() => {});
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = (ev) => setJsonText(ev.target.result);
    reader.readAsText(f);
  };

  const save = async () => {
    if (!propertyId.trim()) { toast.error("Enter the numeric GA4 Property ID"); return; }
    if (!jsonText.trim()) { toast.error("Upload the service-account JSON key file"); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/marketing/config`, { property_id: propertyId.trim(), service_account_json: jsonText });
      toast.success(`Saved — connected as ${res.data.client_email}`);
      setJsonText(""); setFileName("");
      loadStatus(); onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save GA4 config");
    } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API}/marketing/config/test`);
      toast.success(`Connection OK — ${res.data.rows_returned} row(s) returned`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally { setTesting(false); }
  };

  const disconnect = async () => {
    try {
      await axios.delete(`${API}/marketing/config`);
      toast.success("GA4 disconnected");
      setPropertyId(""); loadStatus(); onChange?.();
    } catch (e) { toast.error("Failed to disconnect"); }
  };

  const configured = status?.configured;
  return (
    <div className="space-y-4" data-testid="ga4-setup">
      {/* Status */}
      <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: configured ? "#0E6B3F19" : "#8A857A19" }}>
            <Globe className="w-5 h-5" style={{ color: configured ? "#0E6B3F" : "#8A857A" }} />
          </div>
          <div className="flex-1">
            <div className="text-[#1A1A1A] font-medium flex items-center gap-2">
              Google Analytics 4
              <Badge className="border-0 text-[10px]" style={{ background: configured ? "#0E6B3F19" : "#8A857A19", color: configured ? "#0E6B3F" : "#8A857A" }}>
                {configured ? "Connected" : "Not connected"}
              </Badge>
              {status?.source && <span className="text-[10px] text-[#8A857A]">via {status.source}</span>}
            </div>
            <div className="text-xs text-[#8A857A] mt-0.5">
              {configured ? `Property ${status.property_id} · ${status.client_email}` : "Upload your service-account key and Property ID below."}
            </div>
          </div>
          {configured && (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={test} disabled={testing} className="border-[#DAD5CA] bg-transparent text-[#8A6D00] hover:bg-[#FFF8D6]" data-testid="ga4-test">
                {testing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />} Test
              </Button>
              <Button size="sm" variant="outline" onClick={disconnect} className="border-[#DAD5CA] bg-transparent text-[#C23A00] hover:bg-[#FFE9E0]" data-testid="ga4-disconnect">
                <Trash2 className="w-4 h-4 mr-1" /> Disconnect
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload form */}
      <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
        <CardContent className="p-4 space-y-3">
          <div className="text-xs uppercase tracking-wider text-[#8A857A] font-semibold">{configured ? "Replace credentials" : "Connect Google Analytics 4"}</div>
          <div>
            <label className="text-xs text-[#59554D]">GA4 Property ID (numeric)</label>
            <Input value={propertyId} onChange={(e) => setPropertyId(e.target.value)} placeholder="362610285"
              className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="ga4-property-id" />
          </div>
          <div>
            <label className="text-xs text-[#59554D]">Service-account key (.json)</label>
            <div className="flex items-center gap-3 mt-1">
              <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[#DAD5CA] bg-[#F4F2ED] text-sm text-[#59554D] hover:bg-[#222]" data-testid="ga4-file-label">
                <Plus className="w-4 h-4" /> Choose file
                <input type="file" accept="application/json,.json" className="hidden" onChange={onFile} data-testid="ga4-file-input" />
              </label>
              <span className="text-xs text-[#8A857A]">{fileName || "No file selected"}</span>
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} className="bg-[#1D4ED8] hover:bg-[#EFF6FF] text-[#1A1A1A] font-medium" data-testid="ga4-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />} Save &amp; Connect
            </Button>
          </div>
          <div className="rounded-lg border border-[#FFF8D6] bg-[#FFF8D6] p-3 flex items-start gap-2 text-xs text-[#59554D]">
            <Info className="w-4 h-4 text-[#8A6D00] shrink-0 mt-0.5" />
            <span>
              In Google Cloud: enable the Analytics Data API, create a service account and download its JSON key.
              Then in GA4 → Admin → Property Access Management, add that service account's email as a <b>Viewer</b>.
              Upload the key here — stored securely in the database and used on both preview and the live site (no redeploy needed).
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function GA4Panel() {
  const [report, setReport] = useState("traffic");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    setLoading(true); setError(null);
    axios.get(`${API}/marketing/website/${report}?start=28daysAgo&end=yesterday`)
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.data?.detail || "GA4 report failed"))
      .finally(() => setLoading(false));
  }, [report]);
  const isPerm = error && /permission/i.test(error);
  return (
    <div className="space-y-3" data-testid="mk-ga4-panel">
      <div className="flex gap-2 flex-wrap">
        {["traffic", "sessions", "source", "pages", "conversions", "leads"].map(r => (
          <Button key={r} size="sm" variant={report === r ? "default" : "outline"}
            className={report === r ? "bg-[#1D4ED8] text-[#1A1A1A]" : "border-[#DAD5CA] bg-transparent text-[#59554D]"}
            onClick={() => setReport(r)} data-testid={`mk-ga4-${r}`}>{r === "leads" ? "leads by form" : r}</Button>
        ))}
      </div>
      {error ? (
        <div className="rounded-lg border border-[#FFF7D1] bg-[#FFF7D1] p-4 text-sm text-[#59554D]" data-testid="mk-ga4-error">
          <div className="text-[#6B5600] font-medium mb-1">GA4 connected, but access isn't granted yet</div>
          {isPerm ? (
            <span>The service account <b>equipr-intelligence@equipr-web.iam.gserviceaccount.com</b> needs <b>Viewer</b> access on this GA4 property. In GA4 → Admin → Property Access Management → add that email as a Viewer, then refresh.</span>
          ) : <span>{error}</span>}
        </div>
      ) : loading ? (
        <div className="p-6 text-center text-[#8A857A]"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
      ) : (
        <>
          {report === "conversions" && data?.rows?.length > 0 && !data.rows.some(r => parseInt(r.keyEvents || 0, 10) > 0) && (
            <div className="rounded-lg border border-[#FFF8D6] bg-[#FFF8D6] p-3 flex items-start gap-2 text-xs text-[#59554D] mb-3" data-testid="mk-ga4-conversions-notice">
              <Info className="w-4 h-4 text-[#8A6D00] shrink-0 mt-0.5" />
              <span>
                No <b>key events</b> configured yet, so conversions read 0. Two steps: (1) the website fires a lead event on demo/contact form submit
                (e.g. <b>generate_lead</b>, <b>demo_request</b>, <b>contact_us</b> via gtag/GTM); (2) in GA4 → Admin → Key events, mark those events as key events.
                Real numbers then appear here automatically.
              </span>
            </div>
          )}
          {report === "leads" && (!data?.rows || data.rows.length === 0) && (
            <div className="rounded-lg border border-[#EFF6FF] bg-[#EFF6FF] p-3 flex items-start gap-2 text-xs text-[#59554D] mb-3" data-testid="mk-ga4-leads-notice">
              <Info className="w-4 h-4 text-[#1D4ED8] shrink-0 mt-0.5" />
              <span>Breaks <b>generate_lead</b> down by form (event_label: demo, contact, fb_ad_cranes_demo, early_adopter, …). Empty until the first consented form submission fires — then each landing page / form shows its own lead count.</span>
            </div>
          )}
          <div className="overflow-auto rounded-lg border border-[#DAD5CA]">
            <table className="w-full text-sm">
              <tbody>
                {(data?.rows || []).map((row, i) => (
                  <tr key={i} className="border-b border-[#F4F2ED]">
                    {Object.entries(row).map(([k, v]) => <td key={k} className="px-3 py-2 text-[#59554D]"><span className="text-[#8A857A]">{k}:</span> {v}</td>)}
                  </tr>
                ))}
                {(!data?.rows || data.rows.length === 0) && <tr><td className="px-3 py-6 text-center text-[#8A857A]">No rows for this period.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
